import {
  RadioGroup,
  SecondaryNav,
  LeftPanel,
  SearchInput,
  RightPanel
} from "src/blitz"
import { useSelector, useDispatch } from "react-redux"
import styles from "./ChillerPerformanceReport.module.scss"
import {
  getEquipmentLoader,
  getEquipments,
  getFilter,
  getSelectedEquipments,
  setChillerCharacteristics,
  setSelectedChillerProperties,
  setSelectedEquipments
} from "src/redux/slicers/reportSlice"
import clsx from "clsx"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useParams } from "react-router-dom"
import { selectBuildingSummary } from "src/redux/slicers/buildingSummarySlice"
import { SearchRegex } from "src/utils/CommonMethods"
import { sortByAlphanumericSpecialChars } from "src/blitz/DataGridTableNewDesign/helper"
import { getChillerProperties } from "src/graphql/queries/chart"
import { API } from "src/shared/lib/amplify"
import { getEquipmentSummaryById } from "src/components/equipmentSummary/graphql"
import { v4 as uuidv4 } from "uuid"
import { CURRENT_FETCH } from "../../Charts/constant"
import { selectEquipmentSummary } from "src/redux/slicers/equipmentSummarySlice"
import { TranslateComponent } from "src/common/translations"
import { useActiveDeviceIds } from "src/hooks/useActiveDeviceIds"
import { filterEquipmentByDeviceIds } from "src/utils/analyticsHelper"

const EquipmentList = () => {
  const dispatch = useDispatch()
  const params = useParams()
  const { data: buildingSummary } = useSelector(selectBuildingSummary)
  const { buildingId } = buildingSummary?.buildingId ? buildingSummary : params
  const [searchText, setSearchText] = useState("")
  const equipLoading = useSelector(getEquipmentLoader)
  const allEquipments = useSelector(getEquipments)
  // const equipments = useSelector(getEquipments)
  const {
    activeDeviceIds,
    activeDeviceIdsRef,
    loadingDevices,
    devicesFetched
  } = useActiveDeviceIds(buildingId)
  const selectedItem = useSelector(getSelectedEquipments)
  const { data: selectedEquipment } = useSelector(selectEquipmentSummary)
  const { equipmentType: selectedEquipmentType } = useSelector(getFilter)
  const handleSearch = (value: string) => {
    setSearchText(value)
  }
  const handleEquipmentChange = (ev) => {
    const selectedItem = equipments.find((item) => item?.id === ev) || {}
    dispatch(setSelectedEquipments(selectedItem))
  }

  const equipments = useMemo(() => {
    // While devices are still loading or haven't been fetched, show all so the
    // list is not empty while waiting.
    if (loadingDevices || !devicesFetched) return allEquipments
    // activeDeviceIds is null only when deviceList hasn't been fetched (handled
    // above by !devicesFetched), so this is a safety guard.
    if (activeDeviceIds === null) return []
    // Empty Set means the building has no active automation devices Ã¢â‚¬â€
    // no equipment should be listed.
    if (!activeDeviceIds.size) return []
    return filterEquipmentByDeviceIds(allEquipments, activeDeviceIds)
  }, [allEquipments, activeDeviceIds, loadingDevices, devicesFetched])

  const fetchChillerProperties = useCallback(async (equipmentId) => {
    try {
      const newFetch = uuidv4()
      dispatch(
        setChillerCharacteristics({
          type: "Loading",
          [CURRENT_FETCH]: newFetch
        })
      )
      const graphQLParams: any = {
        authMode: "userPool",
        query: getChillerProperties,
        variables: { id: equipmentId }
      }
      const apiData: any = await API.graphql(graphQLParams)
      if (apiData && !apiData.errors) {
        const characteristics =
          apiData?.data?.getPropertyMappingByEquipmentID?.items
        return { characteristics, newFetch }
      }
    } catch (err) {
      dispatch(setChillerCharacteristics({ type: "Failure" }))
    }
  }, [])

  const fetchEquipmentDetails = useCallback(async (equipmentId) => {
    try {
      const graphQLParams: any = {
        authMode: "userPool",
        query: getEquipmentSummaryById,
        variables: { equipmentId: equipmentId }
      }
      const chillerEquipmentDetails: any = await API.graphql(graphQLParams)
      if (chillerEquipmentDetails && !chillerEquipmentDetails.errors) {
        if (chillerEquipmentDetails?.data?.getEquipment) {
          return chillerEquipmentDetails?.data?.getEquipment || {}
        }
      }
    } catch (err) {
      dispatch(setChillerCharacteristics({ type: "Failure" }))
    }
  }, [])

  useEffect(() => {
    if (selectedItem?.id) {
      dispatch(setSelectedChillerProperties([]))
      const fetchCharacteristics = async () => {
        const [response, equipmentDetails] = await Promise.all([
          fetchChillerProperties(selectedItem?.id),
          fetchEquipmentDetails(selectedItem?.id)
        ])
        dispatch(
          setChillerCharacteristics({
            type: "Success",
            data: response?.characteristics || [],
            [CURRENT_FETCH]: response?.newFetch,
            equipmentId: selectedItem?.id,
            equipmentDetails: equipmentDetails
          })
        )
      }
      fetchCharacteristics()
    }
  }, [selectedItem])

  const filteredEquipments = useMemo(() => {
    let tempEquipments = equipments
    if (searchText) {
      tempEquipments = equipments.filter(({ name }) =>
        SearchRegex(name, searchText)
      )
    }
    tempEquipments = sortByAlphanumericSpecialChars(
      "name",
      "Asc",
      tempEquipments
    )
    return tempEquipments?.map((equipment: any) => ({
      name: equipment.name,
      value: equipment.id
    }))
  }, [equipments, searchText, selectedItem])

  useEffect(() => {
    if (
      selectedEquipment &&
      selectedEquipment?.type === selectedEquipmentType?.value
    ) {
      const preSelectedEquipment =
        equipments.find((item) => item?.id === selectedEquipment?.id) || {}
      dispatch(setSelectedEquipments(preSelectedEquipment))
    }
  }, [selectedEquipment, equipments])
  return (
    <div className={clsx("col-sm", styles.reportListWrapper)}>
      <div className={clsx(styles.reportPanel)}>
        <SecondaryNav withBorder>
          <LeftPanel loading={equipLoading}>
            <TranslateComponent>Select System or Equipment</TranslateComponent>
          </LeftPanel>
          <RightPanel loading={equipLoading}>
            <SearchInput
              onInputChange={handleSearch}
              onInputClear={() => setSearchText("")}
              searchText={searchText}
            />
          </RightPanel>
        </SecondaryNav>
        <div className={styles.panelList}>
          <div className={styles.panelListInner}>
            <div className={styles.reportListBody}>
              <RadioGroup
                radioGroupClassName={styles.radioButtonGroup}
                options={filteredEquipments}
                layout="column"
                handleChange={handleEquipmentChange}
                selected={selectedItem?.id}
                highLightText={searchText}
                translateCurrentValueOff={true}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EquipmentList
